import {
  Account,
  Bot, BotResponse,
  Notification,
  NotificationResponse, StrategiesResponse,
  Ticker,
  UserResponse,
  UserSettings,
} from "../_app/types";
import Api from "../class/Api";
import { HubError } from "../class/Error";
import { showNotification } from "../store/slices/notificationSlice";
import { AppDispatch } from "../store/store";
import Keycloak from "keycloak-js";
import { setUser, switchAccount } from "../store/slices/userSlice";
import { APP_ID } from "../_app/consts";
import { jwtDecode } from "jwt-decode";
import { useApi } from "../_hooks/use-api";
import { Strategy } from "../store/slices/strategySlice";

function sortTickers(data: Ticker[]): Ticker[] {
  return data.sort((a, b) => {
    if (a.quote === "USDT" && b.quote !== "USDT") {
      return -1;
    } else if (a.quote !== "USDT" && b.quote === "USDT") {
      return 1; // a (not USDT) should come after b (USDT)
    }

    // If both are USDC or both are USDT, maintain original order
    return 0;
  });
}

export async function fetchBots(
  api: ReturnType<typeof useApi>,
  accountId: string,
  profileId: string,
  status: string,
  page: number,
  sort: string,
): Promise<Bot[]> {
  try {
    const response = await api.getBots(accountId, status.toUpperCase(), profileId, page, sort);
    return response.bots;
  } catch (error) {
    throw error;
  }
}

export async function fetchAutoStrategy(
  api: ReturnType<typeof useApi>,
  accountId: string,
  profileId: string,
  status: string,
  page: number,
  sort: string
): Promise<Strategy[]> {
  try {
    const response = await api.getStrategies(accountId, profileId, status.toUpperCase(), page, sort);
    return response.strategies;
  } catch (error) {
    throw error;
  }
}

export async function loadTickers(market: string, api: ReturnType<typeof useApi>) {
  try {
    const response: any = await api.getTickers(market.toUpperCase(), "FUTURES", 0, 700);
    const other = response.other.data;
    const ranked = response.ranked.data;

    const sortedRanked = sortTickers(ranked);
    // const rankedUniq = ranked.filter((ticker: Ticker) => ticker.quote !== 'USDC');
    const mergedTickers: Ticker[] = sortedRanked.concat(other);

    return mergedTickers as Ticker[];
  } catch (error: any) {
    console.error(error);
  }
  return [];
}

export async function _getTickers(
  api: ReturnType<typeof useApi>,
  setValue: any,
  market: string,
  dispatch?: AppDispatch,
  loading?: (value: boolean) => void
) {
  loading && loading(true);
  try {
    const response: any = await api.getTickers(market.toUpperCase(), "FUTURES", 0, 500);
    const other = response.other.data;
    const ranked = response.ranked.data;

    const sortedRanked = sortTickers(ranked);
    const mergedTickers: Ticker[] = sortedRanked.concat(other);

    setValue(mergedTickers);
  } catch (error: any) {
    if (dispatch) {
      error instanceof HubError
        ? dispatch(
            showNotification({
              message: `${error.message} ${error.detailedMessage}`,
              severity: "error",
            })
          )
        : dispatch(showNotification({ message: error.message, severity: "error" }));
    } else {
      console.error(error);
    }
  } finally {
    loading && loading(false);
  }
}

export const loginWithPasswordGrant = async (
  username: string,
  password: string,
  keycloak: Keycloak
) => {
  const params = new URLSearchParams();
  params.append("grant_type", "password");
  params.append("client_id", keycloak.clientId ?? ""); // keycloak.clientId
  params.append("username", username);
  params.append("password", password);

  try {
    const response = await fetch(
      `${keycloak.authServerUrl}/realms/${keycloak.realm}/protocol/openid-connect/token`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: params,
      }
    );

    if (!response.ok) {
      throw new Error("Failed to fetch token");
    }

    const data = await response.json();

    if (keycloak) {
      // Сохраняем токены в Keycloak
      keycloak.token = data.access_token;
      keycloak.refreshToken = data.refresh_token;
    }

    if (data) {
      localStorage.setItem("token" + APP_ID, data.access_token);
      localStorage.setItem("refreshToken" + APP_ID, data.refresh_token as string);
    }

    // Устанавливаем таймер обновления токена
    // setTokenRefreshTimer(data.expires_in);

    return data;
  } catch (error) {
    console.error("Error obtaining token:", error);
    return "";
  }
};

export async function getLastEntityLeverage(
  api: ReturnType<typeof useApi>,
  accountId: string,
  profileId: string,
  type: 'bots' | 'strategies',
) {
  try {
    const isBots = type === 'bots';
    const activeResult = isBots
      ? await api.getBots(accountId, 'ACTIVE', profileId, 0, 'createdAt:desc', 'FUTURES_GRID', 1)
      : await api.getStrategies(accountId, profileId, 'ACTIVE', 0, 'startTime:desc', 1);

    const activeEntities = isBots
      ? (activeResult as BotResponse).bots : (activeResult as StrategiesResponse).strategies;

    if (activeEntities && activeEntities.length > 0) {
      return activeEntities[0].leverage as number;
    } else {
      const archivedResult = isBots
        ? await api.getBots(accountId, 'ARCHIVED', profileId, 0, 'closeTime:desc', 'FUTURES_GRID', 1)
        : await api.getStrategies(accountId, profileId, 'ARCHIVED', 0, 'closeTime:desc', 1);

      const archivedEntities = isBots
        ? (archivedResult as BotResponse).bots : (archivedResult as StrategiesResponse).strategies;

      if (archivedEntities && archivedEntities.length > 0) {
        return archivedEntities[0].leverage as number;
      } else {
        return 1;
      }
    }
  } catch (error: any) {
    return 1;
  }
}

export async function getProfile(
  token: string,
  dispatch: AppDispatch,
  navigate: any,
  api: ReturnType<typeof useApi>
) {
  try {
    const { profileId }: { profileId: string } = jwtDecode(token);
    const response: UserResponse = await api.getUserProfile(profileId);
    if (response.settings) {
      const activeAccount = response.settings.find((item) => item.name === "ACTIVE_ACCOUNT_ID");
      if (activeAccount && response.accounts) {
        const accountToSwitch = response.accounts.find((item) => item.id === activeAccount.value);
        accountToSwitch
          ? dispatch(switchAccount(accountToSwitch))
          : dispatch(
              switchAccount({
                id: "",
                name: "",
                market: "",
                marginType: "",
                leverage: 0,
                linkDateTime: null,
              })
            );
      }
    }
    dispatch(setUser(response));

    const lastPath = sessionStorage.getItem("lastPath");
    if (
      lastPath !== "/" &&
      lastPath !== "/creation/bot" &&
      lastPath !== "/creation/strategy" &&
      lastPath !== null
    ) {
      navigate.lastPath(lastPath);
    } else {
      navigate.bots();
    }
  } catch (error: any) {
    if (error instanceof HubError) {
      if (error.errorCode === "AGS_02") {
        try {
          const { profileId }: { profileId: string } = jwtDecode(token);
          await api.createUserProfile();
          try {
            const response: UserResponse = await api.getUserProfile(profileId);
            if (response.settings) {
              const activeAccount = response.settings.find(
                (item) => item.name === "ACTIVE_ACCOUNT_ID"
              );
              if (activeAccount && response.accounts) {
                const accountToSwitch = response.accounts.find(
                  (item) => item.id === activeAccount.value
                );
                accountToSwitch && dispatch(switchAccount(accountToSwitch));
              }
            }
            dispatch(setUser(response));
            navigate.bots();
          } catch (error: any) {
            error instanceof HubError
              ? dispatch(
                  showNotification({
                    message: `${error.message} ${error.detailedMessage}`,
                    severity: "error",
                  })
                )
              : dispatch(showNotification({ message: error.message, severity: "error" }));
          }
        } catch (error: any) {
          error instanceof HubError
            ? dispatch(
                showNotification({
                  message: `${error.message} ${error.detailedMessage}`,
                  severity: "error",
                })
              )
            : dispatch(showNotification({ message: error.message, severity: "error" }));
        }
      } else {
        dispatch(
          showNotification({
            message: `${error.message} ${error.detailedMessage}`,
            severity: "error",
          })
        );
      }
    } else {
      console.log(error.message, "error");
    }
  }
}

// export async function getProfile (token: string, dispatch: AppDispatch) {
// 	try {
// 		console.log('export async function getProfile (token: string, dispatch: AppDispatch)');
// 		const {profileId}: {profileId: string} = jwtDecode(token);
// 		console.log(jwtDecode(token));
// 		const fetchProfile = async () => {
// 			const response: UserResponse = await Api.getUserProfile(profileId);
// 			if (response.settings) {
// 				const activeAccount = response.settings.find(
// 					(item) => item.name === "ACTIVE_ACCOUNT_ID"
// 				);
// 				if (activeAccount) {
// 					const accountToSwitch = response.accounts.find(
// 						(item) => item.id === activeAccount.value
// 					);
// 					if (accountToSwitch) {
// 						dispatch(switchAccount(accountToSwitch));
// 					}
// 				}
// 			}
//
// 			dispatch(setUser(response));
// 		};
//
// 		await fetchProfile();
//
// 	} catch (error: any) {
// 		if (error.response?.status === 404) {
// 			try {
// 				const { profileId }: { profileId: string } = jwtDecode(token);
// 				await Api.createUserProfile();
//
// 				const fetchProfile = async () => {
// 					const response: UserResponse = await Api.getUserProfile(profileId);
// 					if (response.settings) {
// 						const activeAccount = response.settings.find(
// 							(item) => item.name === "ACTIVE_ACCOUNT_ID"
// 						);
// 						if (activeAccount) {
// 							const accountToSwitch = response.accounts.find(
// 								(item) => item.id === activeAccount.value
// 							);
// 							if (accountToSwitch) {
// 								dispatch(switchAccount(accountToSwitch));
// 							}
// 						}
// 					}
// 					dispatch(setUser(response));
// 				};
// 				await fetchProfile();
// 			} catch (createError) {
// 				console.log("Ошибка создания профиля:", createError);
// 			}
// 		} else {
// 			console.log("Ошибка получения профиля:", error.message);
// 		}
// 	}
// };

export const fetchNotifications = async (
  api: ReturnType<typeof useApi>,
  accountId: string,
  page: number,
  size: number
): Promise<Notification[]> => {
  try {
    console.log(page);
    const response: NotificationResponse = await api.accountNotifications(accountId, page, size);
    return response.content; // Возвращаем результат
  } catch (error) {
    throw error; // Пробрасываем ошибку дальше
  }
};

export const handleSwitchAccount = async (
  api: ReturnType<typeof useApi>,
  profileId: string,
  dispatch: AppDispatch,
  value: Account,
  onClose?: (value: boolean) => void
) => {
  let settings: UserSettings[] = [{ name: "ACTIVE_ACCOUNT_ID", value: value.id, format: "STRING" }];
  try {
    await api.updateUserSettings(profileId, settings);

    dispatch(
      showNotification({
        message: `Account ${value.name} switched`,
        severity: "info",
      })
    );
    dispatch(switchAccount(value));
    return true;
  } catch (error: any) {
    error instanceof HubError
      ? dispatch(
          showNotification({
            message: `${error.message} ${error.detailedMessage}`,
            severity: "error",
          })
        )
      : dispatch(showNotification({ message: error.message, severity: "error" }));
  } finally {
    onClose && onClose(false);
  }
};
